import { useContext, useEffect } from "react";
import Form from "../../Helpers/Form/Form";
import { popupcontext } from "../../../context/Popupscontext";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import TasksFormBody from "./TasksFormBody";
import { Formik, Form as FormFormik } from "formik";
import * as Yup from "yup";
import Button from "../../Helpers/Button/Button";
import {
  useCreateTaskMutation,
  useEditTaskMutation,
  useGetSingleTaskQuery,
} from "../../../Redux/service/Tasks/Tasks";
import { toast } from "react-toastify";

export default function TasksForm({ requestTask = null }) {
  const { form, isEditing, currentTaskId, setEdited, handleClose } =
    useContext(popupcontext);
  const [createTask] = useCreateTaskMutation();
  const [editTask] = useEditTaskMutation();
  const { refetch, data: editData } = useGetSingleTaskQuery(currentTaskId, {
    skip: !currentTaskId,
  });

  useEffect(() => {
    if (isEditing && currentTaskId) {
      refetch();
    }
  }, [isEditing, form]);

  let initialValues = {};
  if (isEditing && editData?.data) {
    const data = editData?.data;
    console.log(data);
    initialValues = {
      name: data?.name || "",
      type: data?.type || "",
      priority: data?.priority || "medium",
      startTime: data?.startTime || "",
      expectedDuration: data?.expectedDuration || "",
      assignedToId: data?.assignedToId ? String(data.assignedToId) : "",
      poiId: data?.poiId || "",
      forms: data?.forms?.map((f) => f.formId) || [],
      notes: data?.notes || "",
      enableLocationTracking: data?.enableGeofencing || false, // Map from backend field
      flowType: data?.flowType || "sequential",
    };
  } else if (requestTask) {
    initialValues = {
      name: requestTask?.name || "",
      type: requestTask?.type || "",
      priority: "medium",
      startTime: requestTask?.startTime || "",
      expectedDuration: "",
      assignedToId: "",
      poiId: requestTask?.poiId || "",
      forms: [],
      notes: requestTask?.notes || "",
      enableLocationTracking: false, // Default to false for new tasks
      flowType: "sequential",

    };
  } else {
    initialValues = {
      name: "",
      type: "",
      priority: "medium",
      startTime: "",
      expectedDuration: "",
      assignedToId: "",
      poiId: "",
      forms: [],
      notes: "",
      customAttributes: null,
      enableLocationTracking: false, // Default to false for new tasks
    };
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(trans("Task Name is required"))
      .matches(/^[^\s]/, trans("cannot start with a space"))
      .max(128, trans("Task Name cannot exceed 128 characters")),
    type: Yup.string().required(trans("Task type is required")),
    priority: Yup.string().required(trans("Priority is required")),
    expectedDuration: Yup.number()
      .typeError(trans("Duration must be a number"))
      .required(trans("Duration is required"))
      .min(1, trans("Duration must be a positive number")),
    startTime: Yup.date()
      .typeError("Start time must be a valid date")
      .required("Start time is required"),
    assignedToId: Yup.number().nullable(),
    poiId: Yup.number().nullable(),
    forms: Yup.array().of(Yup.number()).nullable(),
    enableLocationTracking: Yup.boolean(), // Add validation for the new field
  });

  const handleSubmit = async (values, setSubmitting, resetForm) => {
    setSubmitting(true);

    const normalizedValues = {
      ...values,
      expectedDuration: Number(values.expectedDuration),
      assignedToId: values.assignedToId ? Number(values.assignedToId) : null,
      poiId: values.poiId ? Number(values.poiId) : null,
      forms: values.forms.map((id) => Number(id)),
      // Map the meaningful frontend name to backend expected name
      enableGeofencing: values.enableLocationTracking,
      isFlexible: values.flowType === "flexible",
    };

    delete normalizedValues.enableLocationTracking;
    delete normalizedValues.flowType;

    if (requestTask?.id) {
      normalizedValues.serviceRequestId = requestTask.id;
    }

    let payload = normalizedValues;
    if (isEditing && editData?.data) {
      const original = {
        name: editData.data?.name || "",
        type: editData.data?.type || "",
        priority: editData.data?.priority || "medium",
        startTime: editData.data?.startTime || "",
        expectedDuration: editData.data?.expectedDuration || "",
        assignedToId: editData.data?.assignedToId || null,
        poiId: editData.data?.poiId || null,
        forms: editData.data?.forms?.map((f) => f.formId) || [],
        notes: editData.data?.notes || "",
        enableGeofencing: editData.data?.enableGeofencing || false, // Include in comparison
      };
      payload = Object.keys(normalizedValues).reduce((acc, key) => {
        const newVal = normalizedValues[key];
        const oldVal = original[key];

        if (Array.isArray(newVal)) {
          if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            acc[key] = newVal;
          }
        } else {
          if (newVal !== oldVal) {
            acc[key] = newVal;
          }
        }
        return acc;
      }, {});
      if (normalizedValues.serviceRequestId) {
        payload.serviceRequestId = normalizedValues.serviceRequestId;
      }
    }

    try {
      if (!isEditing) {
        await createTask(payload).unwrap();
        toast.success(trans("Task created successfully"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
      } else {
        await editTask({
          id: currentTaskId,
          payload: payload,
        }).unwrap();
        toast.success(trans("Task edited successfully"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
      }

      resetForm();
      handleClose("form");
      setEdited(true);
    } catch (err) {
      let errorMsg = trans(err?.data?.message ?? "Something went wrong");
      toast.error(errorMsg, {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Form show={form}>
        <Offcanvas.Header>
          <FormHeader
            title={isEditing ? trans("Edit task") : trans("Add new task")}
          />
        </Offcanvas.Header>
        <div className="H-line"></div>
        <Offcanvas.Body>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values, { setSubmitting, resetForm }) =>
              handleSubmit(values, setSubmitting, resetForm)
            }
            validationSchema={validationSchema}
          >
            {({ handleSubmit, isSubmitting, values: formikValues }) => {
              // Add debugging
              console.log("Formik values:", formikValues);
              
              return (
                <FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
                  <TasksFormBody isEditing={isEditing} editData={editData} />
                  <div className="offcanvas-footer m-0">
                    <div className="offcanvas-footer-body gap-8">
                      <Button
                        type={"primary"}
                        htmlType="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? trans("Saving...") : trans("Save")}
                      </Button>
                      <Button
                        type={"default"}
                        htmlType="button"
                        disabled={isSubmitting}
                        onClick={() => handleClose("form")}
                      >
                        {trans("Cancel")}
                      </Button>
                    </div>
                  </div>
                </FormFormik>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Form>
    </>
  );
}